import Rating from "@components/rating/starRating";
import { Box, Button, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTemplate } from "containers/templateDetail/store";
import sum from "lodash/sum";
import * as React from "react";

const RatioBarContainer = styled("div")(({ theme }) => ({
  height: 10,
  borderRadius: 3,
  // background: theme.palette.common.white,
  background: theme.palette.grey[200],
  position: "relative",
  overflow: "hidden",
}));

const RatioBarInner = styled("div")(({ theme }) => ({
  height: 10,
  position: "absolute",
  top: 0,
  left: 0,
  background: "#faaf00",
}));

const RatingLine = ({ score, ratio }: { score: number; ratio: string }) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box>
        <span style={{ width: 12, display: "inline-block" }}>{score}</span>
        &nbsp;starts
      </Box>
      <Box sx={{ flex: 1, px: 1.5 }}>
        <RatioBarContainer>
          <RatioBarInner style={{ width: `${ratio}%` }} />
        </RatioBarContainer>
      </Box>
      <Box sx={{ width: 40, textAlign: "right" }}>{`${ratio} %`}</Box>
    </Box>
  );
};

const StarRatingBar = ({ score, quantity }) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Rating defaultValue={score} readOnly />
      <Button variant="link" sx={{ ml: 2 }}>
        {quantity} reviews
      </Button>
    </Box>
  );
};

export default function Summary({ total }): React.ReactElement {
  const { review: reviewSummary } = useTemplate();

  const ratings = React.useMemo(() => {
    if (!reviewSummary) {
      return ["0", "0", "0", "0", "0"];
    }

    const calcTotal = sum(reviewSummary.resume);

    if (calcTotal === 0) {
      return ["0", "0", "0", "0", "0"];
    }

    return [
      ((reviewSummary.resume[4] / calcTotal) * 100).toFixed(0),
      ((reviewSummary.resume[3] / calcTotal) * 100).toFixed(0),
      ((reviewSummary.resume[2] / calcTotal) * 100).toFixed(0),
      ((reviewSummary.resume[1] / calcTotal) * 100).toFixed(0),
      ((reviewSummary.resume[0] / calcTotal) * 100).toFixed(0),
    ];
  }, [reviewSummary]);

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        mb: 3,
        flexDirection: {
          xs: "column",
          sm: "row",
          md: "column",
        },
      })}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="h6">Reviews</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            // flexDirection: { xs: "row", sm: "column" },
            flex: 1,
            alignItems: { xs: "flex-end", sm: "flex-start" },
            mb: { xs: 0, sm: 0 },
          }}
        >
          <Typography variant="h3" sx={{ mr: { xs: "auto", sm: 0 } }}>
            {reviewSummary ? reviewSummary.score.toFixed(1) : "5.0"}
          </Typography>
          <Box sx={{ mt: { xs: 2, sm: "auto" }, mr: "auto" }}>
            <StarRatingBar
              score={reviewSummary?.score || 5}
              quantity={total || 0}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ flex: 1, mt: { xs: 3, sm: 0, md: 3 }, pl: { xs: 2, md: 0 } }}>
        <Stack
          spacing={1}
          sx={{ maxWidth: { xs: "auto", sm: 350 }, ml: "auto" }}
        >
          <RatingLine score={5} ratio={ratings[4]} />
          <RatingLine score={4} ratio={ratings[3]} />
          <RatingLine score={3} ratio={ratings[2]} />
          <RatingLine score={2} ratio={ratings[1]} />
          <RatingLine score={1} ratio={ratings[0]} />
        </Stack>
      </Box>
    </Box>
  );
}
